import React, { useEffect, useState, useRef } from 'react';
import { fetchMasterAssetDetails, fetchPolygonSnapshotPrice, fetchOHLC, fetchHistoricalDataForEMA, fetchPolygonSnapshotPriceses, getStoredMarketDetails } from '../composables/fetchPolygon';
import { calculateBollingerBands, calculateSupportResistance, OHLCData } from '../composables/chartUtils';
import StockChart from './StockChart';
import SearchBar from './SearchBar';
import MACDChart from './MACDChart';
import RSIChart from './RSIChart';
import DataManagementTools from './DataManagementTools';
import { exportSignals, importSelectedSignals, requestAnalysis } from '../composables/fetchAPI';
import { toast } from 'react-hot-toast';
import CopySignalTool from './CopySignalTool';
import { useMarketPrices } from '../context/MarketPriceContext'; // ปรับ Path ตามโครงสร้างจริงของคุณ
import { isCryptoSymbol, getStoredCryptoDetails, cryptoPairLabel } from '../composables/fetchCrypto';

// เหรียญราคาต่ำ (เช่น < $1) ต้องโชว์ทศนิยมมากกว่า 2 ตำแหน่ง ไม่งั้นจะเห็นเป็น 0.00
function priceFractionDigits(price: number | null, isCrypto: boolean): number {
  if (!isCrypto || price === null) return 2;
  const abs = Math.abs(price);
  if (abs >= 1) return 2;
  if (abs >= 0.01) return 4;
  return 6;
}
interface TradingViewProps {
  symbol?: string;
  priceLineType: 'freestyle' | 'support-resistance';
  setPriceLineType: (type: 'freestyle' | 'support-resistance') => void;
  onSupportResistanceUpdate?: (
    data: { tag: string; price: number | null } | { tag: string; price: number | null }[]
  ) => void;
  onClearAllSR?: () => void;
  avgBuyPrice?: number | null;
  onIndicatorsReady?: (data: any) => void; // <-- Add this line
  modeLoss: string, // รับ modeLoss จาก HomePage
  setModeLoss: (mode: string) => void, // รับ setModeLoss จาก HomePage
  onPriceClick?: (price: number) => void; // Add this line
  isAdmin?: boolean; // Add this line
  signalData?: any[]; // Add this line to receive signalData from HomePage
  onUserImport: (symbol: string, isBulkImport?: boolean, forceReplace?: boolean) => void; // Add this line to receive onUserImport from HomePage
  showRequestButton?: boolean;
  quotaInfo?: { used: number, total: number, isFull: boolean }; // Add this line to receive quota info from HomePage
  onImportAndVerifyRequest: (symbol: string) => Promise<{ success: boolean; reason?: 'STALE' | 'ERROR' }>; // ✅ เปลี่ยนเป็นตัวใหม่นี้ครับพี่
  onRequestSuccess?: () => void;
  isNewSignalAvailable?: boolean; // conditional เพื่อความปลอดภัย
}
function getStoredToggle<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  const saved = localStorage.getItem(key);
  return saved !== null ? JSON.parse(saved) : defaultValue;
}

async function fetchCompanyName(symbol: string): Promise<string | null> {
  try {
    const details = await fetchMasterAssetDetails(symbol);
    if (details.length > 0) {
      return details[0].name || null;
    }
    return null;
  } catch {
    return null;
  }
}

function filterOhlcByRange(ohlc: any[], range: string) {
  if (!ohlc.length) return [];
  const last = ohlc[ohlc.length - 1];
  const lastDate = new Date(last.t);
  let fromDate = new Date(lastDate);

  switch (range) {
    case '1D':
      fromDate.setDate(lastDate.getDate() - 1);
      break;
    case '5D':
      fromDate.setDate(lastDate.getDate() - 5);
      break;
    case '1M':
      fromDate.setMonth(lastDate.getMonth() - 1);
      break;
    case '6M':
      fromDate.setMonth(lastDate.getMonth() - 6);
      break;
    case 'YTD':
      fromDate = new Date(lastDate.getFullYear(), 0, 1);
      break;
    case '1Y':
      fromDate.setFullYear(lastDate.getFullYear() - 1);
      break;
    case '5Y':
      fromDate.setFullYear(lastDate.getFullYear() - 5);
      break;
    default:
      fromDate.setMonth(lastDate.getMonth() - 6);
  }
  return ohlc.filter(d => new Date(d.t) >= fromDate);
}

function getFetchParamsForRange(range: string) {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  let fromDate = new Date(now);
  let resolution: 'minute' | 'hour' | 'day' = 'minute';
  let multiplier = 1;

  switch (range) {
    case '1D': {
      // If today is Monday, go back to Friday
      const dayOfWeek = now.getDay(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
      if (dayOfWeek === 1) {
        fromDate.setDate(now.getDate() - 3); // Monday -> Friday
      } else if (dayOfWeek === 0) {
        fromDate.setDate(now.getDate() - 2); // Sunday -> Friday
      } else if (dayOfWeek === 6) {
        fromDate.setDate(now.getDate() - 1); // Saturday -> Friday
      } else {
        fromDate.setDate(now.getDate() - 1); // Normal case
      }
      fromDate.setHours(0, 0, 0, 0);
      resolution = 'minute';
      multiplier = 1;
      break;
    }
    case '5D':
      fromDate.setHours(now.getHours() - 180);
      resolution = 'minute';
      multiplier = 30;
      break;
    case '1M':
      fromDate.setMonth(now.getMonth() - 1);
      resolution = 'hour';
      multiplier = 5;
      break;
    case '6M':
      fromDate.setDate(now.getDate() - 180);
      resolution = 'day';
      multiplier = 1;
      break;
    case 'YTD':
      fromDate = new Date(now.getFullYear(), 0, 1);
      resolution = 'day';
      multiplier = 1;
      break;
    case '1Y':
      fromDate.setFullYear(now.getFullYear() - 1);
      resolution = 'day';
      multiplier = 1;
      break;
    case '5Y':
      fromDate.setDate(now.getDate() - (180 * 5));
      resolution = 'day';
      multiplier = 5;
      break;
    default:
      fromDate.setDate(now.getDate() - 180);
      resolution = 'day';
      multiplier = 1;
  }
  const from = fromDate.toISOString().slice(0, 10);
  return { resolution, multiplier, from, to };
}

const TradingView = ({
  symbol = '',
  priceLineType,
  setPriceLineType,
  onSupportResistanceUpdate,
  onClearAllSR,
  signalData, // รับ signalData จาก HomePage
  avgBuyPrice,
  onIndicatorsReady,
  modeLoss, // รับ modeLoss จาก HomePage
  setModeLoss, // รับ setModeLoss จาก HomePage
  onPriceClick,
  isAdmin, // รับ isAdmin จาก HomePage
  onUserImport, // รับ onUserImport จาก HomePage
  showRequestButton,
  quotaInfo,
  onImportAndVerifyRequest,
  onRequestSuccess,
  isNewSignalAvailable = false,
}: TradingViewProps) => {
  const [error, setError] = useState<string | null>(null);
  const [ohlc, setOhlc] = useState<any[]>([]);
  const [chartType, setChartType] = useState<'candlestick' | 'heikin-ashi'>(() => getStoredToggle('ui_chartType', 'heikin-ashi'));
  const [showBollinger, setShowBollinger] = useState(() => getStoredToggle('ui_showBollinger', false));
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [showOverlays, setShowOverlays] = useState(() => getStoredToggle('ui_showOverlays', false));
  const [range, setRange] = useState<'1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y'>(() => getStoredToggle('ui_range', '6M'));
  const [latestDailyClose, setLatestDailyClose] = useState<number | null>(null);
  const [latestPrevDailyClose, setLatestPrevDailyClose] = useState<number | null>(null);
  const [historicalData, setHistoricalData] = useState<OHLCData[]>([]); // New state for historical data
  const [showSLine, setShowSLine] = useState(() => getStoredToggle('ui_showSLine', true));
  const [showRLine, setShowRLine] = useState(() => getStoredToggle('ui_showRLine', true));
  const [showTrendline, setShowTrendline] = useState(() => getStoredToggle('ui_showTrendline', true));
  const [showDashed, setShowDashed] = useState(() => getStoredToggle('ui_showDashed', false));
  const [frequency, setFrequency] = useState<'minute' | 'day' | 'week' | 'month'>(() => getStoredToggle('ui_frequency', 'day'));
  const [showOverlaysMenu, setShowOverlaysMenu] = useState(false);
  const overlaysMenuRef = useRef<HTMLDivElement>(null);
  // 🌟 1. เพิ่มชุดคำสั่งสำหรับ Sync แกน X
  const chartsRef = useRef<{ stock: any, macd: any, rsi: any }>({ stock: null, macd: null, rsi: null });
  const isSyncing = useRef(false);

 // เปลี่ยนชื่อฟังก์ชันและตัวแปรให้เป็น TimeRange เพื่อความไม่งง
const handleTimeRangeChange = (source: 'stock' | 'macd' | 'rsi', timeRange: any) => {
  if (!timeRange || isSyncing.current) return;
  isSyncing.current = true;

  Object.entries(chartsRef.current).forEach(([key, chartInstance]) => {
    if (chartInstance && key !== source) {
      try {
        // ทุกกราฟ subscribe ด้วย visibleLogicalRange (index-based) เหมือนกันหมดแล้ว
        // จึงต้อง apply กลับด้วย setVisibleLogicalRange เท่านั้น ห้ามใช้ setVisibleRange (time-based)
        chartInstance.timeScale().setVisibleLogicalRange(timeRange);
      } catch (e) { }
    }
  });

  requestAnimationFrame(() => {
    isSyncing.current = false;
  });
};


  const dirtyKey = `isDirty_${symbol}`;
  const [livePrice, setLivePrice] = useState<number | null>(null);
  const [providerTimestamp, setProviderTimestamp] = useState<string | null>(null); // 🌟 เพิ่ม State สำหรับเก็บเวลา
  const { currentPrices, fetchPricesForSymbols, isRealtimeConnected } = useMarketPrices();  // 2. ใช้ useEffect ดึงราคา Snapshot ทันทีที่โหลดหน้าจอหรือเปลี่ยน symbol
  // สั่งให้ Context ช่วยเช็คและดึงราคาหุ้นตัวนี้ถ้ายังไม่มีในถัง
  // 🌟 ดึงราคาผ่าน fetchPolygonSnapshotPriceses หรือ Context ทันทีที่เปลี่ยน symbol (รอบเดียว ไม่วนลูป)
  // 🌟 สั่งให้ Context คอยซิงค์ราคาของ symbol นี้ทุกครั้งที่เปลี่ยนหุ้น
  useEffect(() => {
    if (!symbol) return;
    fetchPricesForSymbols([symbol]);

    const marketDetails = getStoredMarketDetails(symbol);
    if (marketDetails && marketDetails.providerTimestampIso) {
      setProviderTimestamp(marketDetails.providerTimestampIso);
    } else {
      setProviderTimestamp(new Date().toISOString());
    }
  }, [symbol, fetchPricesForSymbols]);


  const [canShowBtn, setCanShowBtn] = useState(false);
  // 1. ตรวจสอบว่าหุ้นปัจจุบันมีข้อมูลใน signalData หรือไม่
  const currentSignal = signalData?.find(s => s.ticker === symbol);
  const [isSRLocked, setIsSRLocked] = useState<boolean>(() => {
    const savedLockState = localStorage.getItem('is_sr_locked');
    return savedLockState === 'true';
  });
  const [isReloading, setIsReloading] = useState(false);
  const isButtonDisabled = quotaInfo?.isFull || !showRequestButton;
  const handleManualRefresh = async () => {
    setIsReloading(true);
    try {
      const now = new Date();
      const to = now.toISOString().slice(0, 10);
      let fromDate = new Date(now);

      // ใช้ Logic เดียวกับใน useEffect หลักของคุณ
      if (range === '5Y') {
        fromDate.setFullYear(now.getFullYear() - 5);
      } else if (frequency === 'minute') {
        fromDate.setMonth(now.getMonth() - 1);
      } else {
        fromDate.setFullYear(now.getFullYear() - 5);
      }
      const from = fromDate.toISOString().slice(0, 10);

      let multiplier = 1;
      if (frequency === 'minute') multiplier = 1;

      // 1. ดึงข้อมูลราคาใหม่
      const historical = await fetchOHLC(symbol, frequency, multiplier, from, to);

      // 2. ดึงข้อมูล EMA/Historical สำหรับเครื่องมือคำนวณ
      const emaHistorical = await fetchHistoricalDataForEMA(symbol);

      setOhlc(historical);
      setHistoricalData(emaHistorical);

      toast.success(`อัปเดตข้อมูล ${cryptoPairLabel(symbol)} ล่าสุดเรียบร้อย`);
    } catch (err) {
      toast.error("ไม่สามารถโหลดข้อมูลใหม่ได้");
      console.error(err);
    } finally {
      setIsReloading(false);
    }
  };

  useEffect(() => {
    localStorage.setItem('is_sr_locked', isSRLocked.toString());
  }, [isSRLocked]);

  // จำค่า Toolbar ไว้ใน localStorage เพื่อให้ค้างไว้เหมือนเดิมทุกครั้งที่เปิดหน้าใหม่
  useEffect(() => { localStorage.setItem('ui_chartType', JSON.stringify(chartType)); }, [chartType]);
  useEffect(() => { localStorage.setItem('ui_showBollinger', JSON.stringify(showBollinger)); }, [showBollinger]);
  useEffect(() => { localStorage.setItem('ui_showOverlays', JSON.stringify(showOverlays)); }, [showOverlays]);
  useEffect(() => { localStorage.setItem('ui_showDashed', JSON.stringify(showDashed)); }, [showDashed]);
  useEffect(() => { localStorage.setItem('ui_showSLine', JSON.stringify(showSLine)); }, [showSLine]);
  useEffect(() => { localStorage.setItem('ui_showRLine', JSON.stringify(showRLine)); }, [showRLine]);
  useEffect(() => { localStorage.setItem('ui_showTrendline', JSON.stringify(showTrendline)); }, [showTrendline]);
  useEffect(() => { localStorage.setItem('ui_range', JSON.stringify(range)); }, [range]);
  useEffect(() => { localStorage.setItem('ui_frequency', JSON.stringify(frequency)); }, [frequency]);

  useEffect(() => {
    if (!showOverlaysMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (overlaysMenuRef.current && !overlaysMenuRef.current.contains(e.target as Node)) {
        setShowOverlaysMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOverlaysMenu]);

  useEffect(() => {
    setCanShowBtn(false);
    const timer = setTimeout(() => {
      setCanShowBtn(true);
    }, 3000); // รอ 3 วินาทีให้ StockChart โหลดเส้นเก่าเสร็จก่อน
    return () => clearTimeout(timer);
  }, [symbol]);

  // อ่านค่าเริ่มต้น: ถ้าใน Storage เป็น 'true' ให้โชว์ปุ่มเลย
  const [showPublishBtn, setShowPublishBtn] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(`isDirty_${symbol}`) === 'true';
    }
    return false;
  });
  useEffect(() => {
    const savedDirty = localStorage.getItem(`isDirty_${symbol}`);
    // บังคับให้ State ตรงกับ Storage เป๊ะๆ
    setShowPublishBtn(savedDirty === 'true');
  }, [symbol]);

  // // 3. (สำคัญมาก) สั่งบันทึกสถานะกลับลง Storage เมื่อมีคนสั่ง setShowPublishBtn(true)
  // useEffect(() => {
  //   if (showPublishBtn) {
  //     localStorage.setItem(`isDirty_${symbol}`, 'true');
  //   }
  //   // เราไม่จัดการ removeItem ที่นี่ เพราะจะไปทำที่ handleAdminPublish แทน เพื่อความชัวร์
  // }, [showPublishBtn, symbol]);

  // ทุกครั้งที่ showPublishBtn เปลี่ยน ให้บันทึกลง Storage ด้วย
  // useEffect(() => {
  //   if (showPublishBtn) {
  //     localStorage.setItem(dirtyKey, 'true');
  //   } else {
  //     localStorage.removeItem(dirtyKey);
  //   }
  // }, [showPublishBtn, symbol]);

  // const [isDirty, setIsDirty] = useState(false); // true คือมีการแก้ไขที่ยังไม่ได้ส่ง
  const [showIndicatorsMobile, setShowIndicatorsMobile] = useState<boolean>(() =>
    getStoredToggle('ui_showIndicatorsMobile', true) // Default เป็น false (ปิดไว้ก่อน)
  );
  // console.log(isAdmin, "isAdmin in TradingView");
  // 3. ใช้ useEffect เพื่อ Save ค่าลง Storage เมื่อมีการเปลี่ยนแปลง
  useEffect(() => {
    localStorage.setItem('ui_showIndicatorsMobile', JSON.stringify(showIndicatorsMobile));
  }, [showIndicatorsMobile]);


  // เพิ่มฟังก์ชันนี้ไว้ใน TradingView.tsx หรือส่วนที่ Admin กด Export
  const handleAdminPublish = async () => {
    try {
      // 1. ดึงข้อมูลจาก Local Storage ตาม symbol ปัจจุบัน
      const storageKey = `chartLines_${symbol}`;
      const savedData = localStorage.getItem(storageKey);

      if (!savedData) {
        alert(`ไม่พบข้อมูลเส้นแนวรับ-แนวต้านของ ${cryptoPairLabel(symbol)} ในเครื่อง`);
        return;
      }

      const parsedData = JSON.parse(savedData);

      // 2. เตรียม Payload สำหรับส่งไปที่ /signals/export
      // โครงสร้างตามที่คุณต้องการ: ticker, guruId, และรายละเอียดเส้นต่างๆ
      const payload = {
        ticker: symbol,
        guruId: "แอ๊ดอั้ม", // หรือ ID ของคุณ
        details: {
          lines: parsedData.lines || [],
          supportTags: parsedData.supportTags || [],
          resistanceTags: parsedData.resistanceTags || [],
          lastUpdated: new Date().toISOString()
        }
      };

      const result = await exportSignals(payload);
      if (result) {
        setShowPublishBtn(false);
        localStorage.removeItem(`isDirty_${symbol}`);

        // ✅ เพิ่มบรรทัดนี้: จำสถานะเส้นล่าสุดที่เพิ่งเซฟไป เพื่อเอาไว้เทียบ
        const currentLinesOnly = payload.details.lines.map((l: any) => ({ tag: l.tag, price: l.price }));
        localStorage.setItem(`last_exported_${symbol}`, JSON.stringify(currentLinesOnly));

        toast.success("Publish สำเร็จ ข้อมูลบน Server อัปเดตแล้ว");
      }
    } catch (err: any) {
      console.error("Publish Error:", err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };


  // For lower indicator (demo: closing price line)
  const filteredOhlc = ohlc; // Always use full 5-year data
  const lowerIndicatorData = filteredOhlc.map(item => ({ time: Math.floor(item.t / 1000), value: item.c }));

  // Calculate overlays
  const bollingerBands = showBollinger ? calculateBollingerBands(ohlc) : [];
  const supportResistance = priceLineType === 'support-resistance' ? calculateSupportResistance(ohlc) : { support: [], resistance: [] };

  useEffect(() => {
    const fetchData = async () => {
      const now = new Date();
      const to = now.toISOString().slice(0, 10);
      let fromDate = new Date(now);
      // Special case: 1D range with hour frequency

      // If user selects 5Y, always fetch 5 years
      if (range === '5Y') {
        fromDate.setFullYear(now.getFullYear() - 5);
      } else if (frequency === 'minute') {
        fromDate.setMonth(now.getMonth() - 1); // last 1 month
      } else {
        fromDate.setFullYear(now.getFullYear() - 5); // default 5 years
      }
      const from = fromDate.toISOString().slice(0, 10);

      let multiplier = 1;
      if (frequency === 'minute') multiplier = 1;
      if (frequency === 'week') multiplier = 1;
      if (frequency === 'month') multiplier = 1;

      const historical = await fetchOHLC(symbol, frequency, multiplier, from, to);
      setOhlc(historical);

      setError(historical.length === 0 ? 'No price data available' : null);
    };

    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [symbol, frequency, range]);


  useEffect(() => {
    // 🌟 หมายเหตุ: เดิมทีเอฟเฟกต์นี้เคยเรียก setOhlc() ด้วยข้อมูลรายวัน (daily) แบบ hardcode
    // ซึ่งจะแข่งกัน (race condition) กับเอฟเฟกต์ fetchData ด้านล่างที่ดึงข้อมูลตาม frequency/range จริง
    // ทำให้เวลาเปลี่ยนเป็น weekly แล้วข้อมูลถูก daily fetch ที่ resolve ทีหลังทับซ้อน เกิดกราฟเพี้ยน/กระโดด
    // จึงเหลือเฉพาะการดึง historicalData สำหรับคำนวณ EMA เท่านั้น ไม่ยุ่งกับ state ohlc
    const fetchDatas = async () => {
      try {
        const historical = await fetchHistoricalDataForEMA(symbol);
        setHistoricalData(historical);
      } catch (err) {
        console.error('Error fetching historical EMA data:', err);
      }
    };

    fetchDatas();
  }, [symbol]);

  const isFirstRangeEffect = useRef(true);
  useEffect(() => {
    // ข้ามรอบแรกตอน mount เพื่อไม่ให้ทับค่า frequency ที่จำไว้จาก localStorage
    if (isFirstRangeEffect.current) {
      isFirstRangeEffect.current = false;
      return;
    }
    // ปรับ frequency อัตโนมัติเมื่อ range เปลี่ยน (จากการกดของผู้ใช้)
    if (range === '1D') setFrequency('minute');
    else if (range === '5D') setFrequency('day');
    else if (range === '1M') setFrequency('day');
    else if (range === '6M' || range === 'YTD' || range === '1Y') setFrequency('day');
    else if (range === '5Y') setFrequency('day');
  }, [range]);

  useEffect(() => {
    setCompanyLoading(true);
    setCompanyName(null);
    fetchCompanyName(symbol).then(name => {
      setCompanyName(name);
      setCompanyLoading(false);
    });
  }, [symbol]);

  useEffect(() => {
    // Always fetch the latest 5 daily closes for "วันนี้" reference
    const fetchLatestDaily = async () => {
      const today = new Date();
      const to = today.toISOString().slice(0, 10);
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() - 7); // fetch last 7 days to be safe
      const from = fromDate.toISOString().slice(0, 10);
      const daily = await fetchOHLC(symbol, 'day', 1, from, to);

      // Filter out days with no close price (e.g., weekends/holidays)
      const validCloses = daily.filter(d => typeof d.c === 'number' && d.c !== null);

      if (validCloses.length >= 2) {
        setLatestPrevDailyClose(validCloses[validCloses.length - 2].c);
        setLatestDailyClose(validCloses[validCloses.length - 1].c);
      } else if (validCloses.length === 1) {
        setLatestPrevDailyClose(null);
        setLatestDailyClose(validCloses[0].c);
      }
    };
    fetchLatestDaily();

  }, [symbol]);

  const snapshotPrice = currentPrices[symbol] || null;
  const lastCandlePrice = snapshotPrice !== null ? snapshotPrice : latestDailyClose;

  const prevClose = latestPrevDailyClose;

  // Crypto: เทรด 24/7 ตัดวันที่ 00:00 UTC (07:00 เวลาไทย) และไม่มีระบบแนวรับ-แนวต้าน/Request ของหุ้น
  const isCrypto = isCryptoSymbol(symbol);
  const cryptoDetails = isCrypto ? getStoredCryptoDetails(symbol) : null;
  const priceDigits = priceFractionDigits(lastCandlePrice, isCrypto);

  const todayClose = lastCandlePrice;



  const priceChange = todayClose !== null && prevClose !== null ? todayClose - prevClose : null;
  const percentChange = priceChange !== null && prevClose !== 0 && prevClose !== null
    ? (priceChange / prevClose) * 100
    : null;

  // For range-based change, use the first candle in filteredOhlc (start of range)
  const todayDateStr = new Date().toISOString().slice(0, 10);
  const rangeStartCandle = filteredOhlc.find(d => {
    const dDateStr = new Date(d.t).toISOString().slice(0, 10);
    return dDateStr !== todayDateStr;
  });
  const rangeStartPrice = rangeStartCandle ? rangeStartCandle.c : null;

  const rangeChange = latestDailyClose !== null && rangeStartPrice !== null
    ? latestDailyClose - rangeStartPrice
    : null;
  const rangePercentChange = rangeChange !== null && rangeStartPrice !== 0 && rangeStartPrice !== null
    ? (rangeChange / rangeStartPrice) * 100
    : null;

  // Helper for range label
  const rangeLabelMap: Record<string, string> = {
    '1D': 'วันนี้',
    '5D': '5วันที่ผ่านมา',
    '1M': 'เดือนที่ผ่านมา',
    '6M': '6เดือนที่ผ่านมา',
    'YTD': 'ตั้งแต่ต้นปี',
    '1Y': '1ปีที่ผ่านมา',
    '5Y': '5ปีที่ผ่านมา',
  };
  // 1. ดึงข้อมูลจาก Local Storage มาเตรียมไว้ (หรือถ้าคุณมี state ที่เก็บเส้นพวกนี้อยู่แล้วก็ใช้ state ได้เลย)
  const storageKey = `chartLines_${symbol}`;
  const localData = JSON.parse(localStorage.getItem(storageKey) || '{"lines":[], "supportTags":[], "resistanceTags":[]}');

  const handleRequest = async () => {
    // บล็อกสิทธิ์ป้องกันความปลอดภัยกรณี Prop ตกหล่น
    if (!onImportAndVerifyRequest) {
      console.error("onImportAndVerifyRequest is required");
      return;
    }

    try {
      // 🚀 สั่งรันด่านตรวจ: แอบส่องข้อมูลแอดมินล่าสุดและจำลองการนำเข้าทันที
      const checkResult = await onImportAndVerifyRequest(symbol);

      if (!checkResult.success) {
        if (checkResult.reason === 'STALE') {
          // 💡 แจ้งเตือนข้อความตามที่พี่ต้องการเป๊ะๆ
          toast.error("มีการอัปเดตที่คุณยังไม่ได้ Import ระบบจะทำการอัปเดตข้อมูลใหม่", {
            duration: 8000,
            icon: '⚠️'
          });

          // 🔄 ทำการรีโหลดเพื่อให้หน้าจอวาดเส้นหนา/เส้นบาง และตารางคำนวณใหม่ทันที
          setTimeout(() => window.location.reload(), 1500);
        } else {
          toast.error("เกิดข้อผิดพลาดในการตรวจสอบราคาปัจจุบัน");
        }
        return; // 🛑 บล็อกประตูตายตัว ไม่ส่ง Request ซ้ำซ้อนไปฝั่ง Backend แน่นอน!
      }

      // 🟢 ด่านตรวจผ่านฉลุย (เส้นขาดแคลนจริงๆ หรือเป็นหุ้นใหม่) -> ยิงส่งคำขอจริงไป Backend
      await requestAnalysis({ ticker: symbol });
      toast.success(`ส่งคำขอวิเคราะห์ ${cryptoPairLabel(symbol)} เรียบร้อย!`);

      if (onRequestSuccess) {
        onRequestSuccess();
      }

    } catch (error: any) {
      console.error("Request Error:", error);
      const errorCode = error?.response?.data?.code || error?.code;

      if (errorCode === 400204) {
        toast.error("หุ้นตัวนี้มีการอัปเดตข้อมูลภายใน 30 วันที่ผ่านมาแล้ว", { duration: 8000, icon: '⚠️' });
      } else if (errorCode === 400201) {
        toast.error("คุณขอ Request เกิน limit แล้ว(3/3)", { duration: 8000, icon: '⚠️' });

      } else if (errorCode === 400203) {
        toast.error("หุ้นตัวนี้มีคำขอวิเคราะห์ที่อยู่ระหว่างรอดำเนินการแล้ว", { duration: 8000, icon: '⚠️' });
      } else {
        toast.error(error?.response?.data?.message || "เกิดข้อผิดพลาดในการส่งคำขอ");
      }
    }
  };



  // console.log(showRequestButton, "showRequestButton in TradingView");
  return (
    <div className="w-full flex justify-center relative">
      <div className="w-full max-w-7xl rounded-2xl shadow-2xl px-3 md:px-5 flex flex-col gap-4 py-2"
        style={{ backgroundColor: 'var(--component-bg-color)' }}
      >
        {/* ข้อความลอยอยู่มุมขวาบน */}
        {modeLoss === 'damage-view' && (
          <div className="absolute top-32 right-2 bg-black bg-opacity-50 text-white font-bold text-xl px-2 py-1 rounded z-10">
            คลิกเพื่อดูความเสียหาย (เฉพาะขาลง)
          </div>
        )}
        {/* --- ส่วนที่ 1: Header (ชื่อหุ้น & ราคา) --- */}
        <div className="flex flex-row justify-between items-start border-b border-gray-800  gap-4">

          {/* ฝั่งซ้าย: ชื่อหุ้นและบริษัท (ให้ขยายกว้างได้แต่ไม่เบียดราคา) */}
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap sm:flex-nowrap">
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[var(--text-color)] leading-none">
                {cryptoPairLabel(symbol)}
              </h1>
              {/* {isCrypto && (
                <span className="self-center text-[10px] font-semibold text-orange-400 border border-orange-500/60 bg-orange-500/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                  Crypto · 24/7
                </span>
              )} */}
              {/* ใช้ truncate เพื่อกันชื่อยาวเกินจนล้นบรรทัด */}
              <span className="text-sm font-semibold text-[var(--yellow-text-color)] hidden sm:block truncate opacity-90 max-w-[300px]">
                {companyLoading ? '...' : companyName}
              </span>
            </div>

            {/* ชื่อบริษัทสำหรับ Mobile: จำกัด 1 บรรทัดด้วย truncate */}
            <div className="text-[10px] font-medium text-[var(--yellow-text-color)] sm:hidden uppercase tracking-wider opacity-80 truncate mt-1">
              {companyLoading ? 'Loading...' : companyName}
            </div>

            {/* --- ส่วนที่เพิ่มใหม่ --- */}
            <div className="flex items-center gap-1 border-r border-gray-700 pr-2 mr-1">

              {/* --- ฝั่ง ADMIN: โชว์เครื่องมือจัดการ (Export/Import JSON) --- */}
              {/* คริปโต: เปิดให้แอดมินลอง Export ได้ (ส่ง ticker เป็น X:BTCUSD) — ส่วน Import/Request ของ User ยังปิดอยู่ */}
              {isAdmin && (
                <div className="flex items-center gap-2 md:gap-4 pr-2 md:pr-4 mr-2 md:mr-4">
                  <DataManagementTools
                    isAdmin={true}
                    currentData={{
                      symbol,
                      ...JSON.parse(localStorage.getItem(`chartLines_${symbol}`) || '{"lines":[]}')
                    }}
                    onAdminExportClick={showPublishBtn ? handleAdminPublish : undefined}
                    onImport={() => onUserImport(symbol)}
                  />

                  <div className="flex pl-1.5 md:pl-2 border-l border-gray-700 items-center gap-1">
                    {/* 📱 ซ่อนคำว่า "แนวรับ-ต้าน:" บนมือถือ และแสดงเฉพาะบนจอคอมพิวเตอร์ (md ขึ้นไป) */}
                    <CopySignalTool symbol={symbol} />
                  </div>
                </div>
              )}

              {/* --- ฝั่ง USER: โชว์ปุ่ม Import เฉพาะหุ้น/เหรียญที่แอดมิน Export สัญญาณไว้ --- */}
              {!isAdmin && currentSignal && (
                <div className="flex items-center gap-1 border-r border-gray-700 pr-3 mr-2">
                  {/* 💡 ย้ายคลาส relative มาไว้ที่ div หุ้มปุ่มแทน เพื่อไม่ให้ overflow-hidden ของปุ่มไปตัดป้าย NEW */}
                  <div className="relative group">
                    <button
                      onClick={() => {
                        onUserImport(symbol);
                      }}
                      // 💡 อัปเกรดสีปุ่ม: ถ้ามี Signal ใหม่ให้ปุ่มเป็น Gradient เรืองแสงเตะตา ถ้าไม่มีก็เป็นสีฟ้าปกติ
                      className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg text-xs font-black transition-all duration-300 overflow-hidden relative active:scale-95
          ${isNewSignalAvailable
                          ? 'bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 shadow-[0_0_15px_rgba(59,130,246,0.6)] hover:shadow-[0_0_25px_rgba(59,130,246,0.8)] border border-blue-400'
                          : 'bg-blue-600 hover:bg-blue-500 shadow-md border border-transparent'
                        }
        `}
                    >
                      <span className="group-hover:scale-125 transition-transform duration-300 inline-block">
                        {isNewSignalAvailable ? '✨' : '📥'}
                      </span>
                      <span className="tracking-wide">อัปเดต แนวรับ-แนวต้าน ล่าสุดจากแอดมิน</span>

                      {/* เอฟเฟกต์แสงวิ่งผ่านปุ่มเพื่อให้ User รู้ว่านี่คือปุ่มสำคัญ (อยู่ใต้ overflow-hidden ปลอดภัย) */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out"></div>
                    </button>

                    {/* 🟢 ป้าย NEW ย้ายมาอยู่ข้างนอก button! จะได้ไม่โดนตัด และใส่เงาเรืองแสงสีเขียวเพิ่มความเด่น */}
                    {isNewSignalAvailable && (
                      <span className="absolute -top-2.5 -right-2.5 flex h-5 px-2 bg-[#22c55e] text-black text-[10px] font-black items-center justify-center rounded-md border-2 border-[#1c1c1e] animate-bounce tracking-tighter shadow-[0_0_10px_rgba(34,197,94,0.6)] z-20">
                        NEW
                      </span>
                    )}
                  </div>
                </div>
              )}
              {showRequestButton && !isAdmin && !isCrypto && (
                <div className='flex flex-col  items-center '>
                  <button
                    disabled={isButtonDisabled}
                    onClick={handleRequest}
                    className={`px-2 py-1 rounded font-bold transition-colors ${quotaInfo?.isFull
                      ? 'bg-gray-500 cursor-not-allowed text-gray-300' // ✅ สีเทาเมื่อโควตาเต็ม
                      : isButtonDisabled
                        ? 'hidden' // ซ่อนถ้าแอดมินเพิ่งอัปเดต (ตาม Logic เดิม)
                        : 'bg-blue-600 hover:bg-blue-700 text-white' // ปุ่มปกติ
                      }`}                >

                    <span className='text-xs'>Request แนวรับ-แนวต้าน</span>

                    {/* เอฟเฟกต์แสงวิ่งผ่านปุ่มเพื่อให้ User รู้ว่านี่คือปุ่มสำคัญ */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  </button>
                  <span className="text-[10px] pt-1" style={{ color: 'var(--text-color)' }}>
                    โควต้าวันนี้: {quotaInfo?.used}/{quotaInfo?.total}
                  </span>
                </div>
              )}
            </div>


            {/* ---------------------- */}

          </div>


          {/* ฝั่งขวา: ราคาและการเปลี่ยนแปลง (Fix ขนาดไว้ไม่ให้ขยับตามชื่อบริษัท) */}
          {/* ฝั่งขวา: ราคาและการเปลี่ยนแปลง (Fix ขนาดไว้ไม่ให้ขยับตามชื่อบริษัท) */}
          <div className="flex flex-col items-end flex-shrink-0 text-right">
            <div className="flex flex-row items-end gap-2">
              {/* 🌟 เพิ่ม items-end ตรงนี้ เพื่อให้ลงมาอยู่ระนาบเดียวกับฐานของราคา */}
              <div className="text-[9px] text-gray-400 pb-1.5 tracking-tight">
                {isCrypto ? (
                  cryptoDetails?.providerTimestampIso ? (
                    <span className={cryptoDetails.stale ? 'text-orange-400' : ''}>
                      {isRealtimeConnected && !cryptoDetails.stale && (
                        <span className="inline-flex items-center gap-1 text-green-500 font-semibold mr-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />Live ·
                        </span>
                      )}
                      {cryptoDetails.stale && '⚠ ราคาอาจล่าช้า · '}
                      อัปเดตเมื่อ: {new Date(cryptoDetails.providerTimestampIso).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: cryptoDetails.stale ? 'short' : undefined, timeStyle: 'medium' })} (เวลาไทย)
                    </span>
                  ) : (
                    <span className="text-orange-400">{cryptoDetails?.status === 'UNAVAILABLE' ? 'ยังไม่มีราคาล่าสุด' : 'กำลังซิงค์เวลา...'}</span>
                  )
                ) : providerTimestamp ? (
                  <span>อัปเดตเมื่อ: {new Date(providerTimestamp).toLocaleTimeString('th-TH', { timeZone: 'Asia/Bangkok' })} (เวลาไทย)</span>
                ) : (
                  <span>กำลังซิงค์เวลา...</span>
                )}
              </div>

              <div className="text-2xl md:text-3xl font-black text-[var(--text-color)] leading-none">
                ${lastCandlePrice?.toLocaleString(undefined, { minimumFractionDigits: priceDigits, maximumFractionDigits: priceDigits })}
              </div>
            </div>

            <div className={`text-xs md:text-base font-bold flex items-center justify-end gap-1 mt-1 ${priceChange !== null && priceChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {priceChange !== null ? (
                <>
                  <span className="text-[10px] md:text-xs">{priceChange >= 0 ? '▲' : '▼'}</span>
                  <span className="whitespace-nowrap">
                    {Math.abs(priceChange).toFixed(priceDigits)} ({Math.abs(percentChange || 0).toFixed(2)}%)
                  </span>
                  <span className="text-[10px] text-gray-400 font-normal ml-0.5 self-end mb-0.5">{isCrypto ? 'ตั้งแต่ 07:00 น.' : 'วันนี้'}</span>
                </>
              ) : '...'}
            </div>

            {/* 🌟 แสดงข้อมูล Pre-market / After-hours เฉพาะตอนที่ไม่อยู่ในเวลาปกติ (REGULAR) */}
            {(() => {
              const marketDetails = getStoredMarketDetails(symbol);
              const session = marketDetails.marketSession;
              const extPrice = marketDetails.extendedPrice;

              // ถ้าเป็นเวลาปกติ (REGULAR) หรือไม่มี extendedPrice ให้ซ่อนบรรทัดนี้ทันที
              if (!session || session === "REGULAR" || !extPrice || extPrice <= 0) {
                return null;
              }

              // เทียบกับราคาปิดช่วงตลาดปกติจาก snapshot (price.regular_close) ก่อน
              // ไม่ใช้แท่งเทียนรายวันเป็นหลัก เพราะค่า c ของแท่งอาจไม่ตรงกับ regular_close
              const basePrice = marketDetails.regularClose || latestDailyClose || lastCandlePrice || extPrice;
              const diffVal = extPrice - basePrice;
              const diffPct = basePrice ? (diffVal / basePrice) * 100 : 0;

              let sessionText = session === "PRE_MARKET" ? "ก่อนตลาดเปิด" : "หลังตลาดปิด";

              return (
                <div className="flex items-center gap-1.5 mt-1 text-xs font-semibold">
                  <span className="text-[10px] text-gray-400 font-normal">{sessionText}:</span>
                  <span className="text-[var(--text-color)]">${extPrice.toFixed(2)}</span>
                  <div className={`flex items-center gap-0.5 ${diffVal >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    <span>{diffVal >= 0 ? '▲' : '▼'}</span>
                    <span>{Math.abs(diffVal).toFixed(2)} ({Math.abs(diffPct).toFixed(2)}%)</span>
                  </div>
                </div>
              );
            })()}



            {/* ปุ่ม Reload Data แบบเนียนๆ ข้างชื่อหุ้น */}
            {/* <button
              onClick={handleManualRefresh}
              disabled={isReloading}
              className={`text-xs transition-all active:scale-95 flex items-center gap-1 group ${isReloading ? 'opacity-50' : ''}`}
              title="รีโหลดข้อมูลราคาใหม่"
            >
              <span className="text-gray-400 text-[8px]">กราฟมีปัญหา?</span>
              <span className={` text-[8px] font-bold underline decoration-blue-500/30 underline-offset-2 transition-colors ${isReloading ? 'text-gray-500' : 'text-blue-500 hover:text-blue-400'}`}>
                {isReloading ? 'กำลังโหลด...' : 'คลิกเพื่อรีโหลด'}
              </span>
              {isReloading && <span className="animate-spin text-[8px]">🔄</span>}
            </button> */}
          </div>
        </div>
        {/* Controls */}
        {/* Mobile View: ใช้เป็น Select เพื่อประหยัดพื้นที่ */}
        <div className="grid grid-cols-2 gap-2 md:hidden">
          {/* Group 1: Chart & Lines */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] text-gray-500 font-bold ml-1 uppercase">Price & Chart</span>
            <select
              className="bg-gray-800 text-white text-xs p-2.5 rounded-lg border border-gray-700 outline-none w-full"
              value={chartType}
              onChange={(e) => setChartType(e.target.value as any)}
            >
              <option value="heikin-ashi">📊 Heikin Ashi</option>
              <option value="candlestick">🕯️ แท่งเทียน (Candle)</option>
            </select>
            <select
              className="bg-gray-800 text-white text-xs p-2.5 rounded-lg border border-gray-700 outline-none w-full mt-1"
              value={priceLineType}
              onChange={(e) => setPriceLineType(e.target.value as any)}
            >
              <option value="freestyle">📍 Price Line: Freestyle</option>
              {/* <option value="support-resistance">🤖 Price Line: Auto SR</option> */}
            </select>
          </div>

          {/* Group 2: Overlays (Bollinger, S, R, Trend) */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] text-gray-500 font-bold ml-1 uppercase">Indicators & Overlays</span>
            <select
              className="bg-gray-800 text-white text-xs p-2.5 rounded-lg border border-gray-700 outline-none w-full"
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'bollinger') setShowBollinger(!showBollinger);
                if (val === 'overlays') setShowOverlays(!showOverlays);
                if (val === 'dashed') setShowDashed(!showDashed);
                if (val === 's-line') setShowSLine(!showSLine);
                if (val === 'r-line') setShowRLine(!showRLine);
                if (val === 'trend') setShowTrendline(!showTrendline);
                e.target.value = ""; // reset เพื่อให้กดซ้ำได้
              }}
            >
              <option value="" disabled selected>➕ เพิ่ม/ลด ตัวชี้วัด</option>
              <option value="bollinger">{showBollinger ? '✅' : '➕'} Bollinger Bands</option>
              <option value="overlays">{showOverlays ? '✅' : '➕'} แสดงป้ายราคา</option>
              <option value="dashed">{showDashed ? '✅' : '➕'} เส้นประ</option>
              <option value="s-line">{showSLine ? '✅' : '➕'} เส้นแนวรับ (S)</option>
              <option value="r-line">{showRLine ? '✅' : '➕'} เส้นแนวต้าน (R)</option>
              <option value="trend">{showTrendline ? '✅' : '➕'} เส้นแนวโน้ม (Trend)</option>
            </select>

            {/* Timeframe Selectors (Range & Freq) */}
            <div className="flex gap-1 mt-1">
              <select className="bg-gray-800 text-white text-[10px] p-2 rounded border border-gray-700 outline-none flex-1" value={range} onChange={(e) => setRange(e.target.value as any)}>
                {/* {['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y'].map(r => <option key={r} value={r}>{r}</option>)} */}
                {['6M'].map(r => <option key={r} value={r}>{r}</option>)}

              </select>
              <select className="bg-gray-800 text-white text-[10px] p-2 rounded border border-gray-700 outline-none flex-1" value={frequency} onChange={(e) => setFrequency(e.target.value as any)}>
                {/* {['minute', 'day', 'week', 'month'].map(f => <option key={f} value={f}>{f}</option>)} */}
                {['day', 'week'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <button
                className={`p-2 rounded text-sm flex items-center justify-center transition-all duration-300 ${isSRLocked
                  ? 'bg-yellow-500 text-black shadow-[0_0_12px_rgba(234,179,8,0.6)]' // โหมดล๊อค: สีเหลืองสว่าง + เรืองแสง
                  : 'bg-gray-700 text-white hover:bg-gray-600' // โหมดปลดล๊อค: สีเทาปกติ
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  // 💡 แก้ตรงนี้! ใช้ !isSRLocked เพื่อสลับค่าไปมาแทน e.target.checked
                  setIsSRLocked(!isSRLocked);
                }}
                title={isSRLocked ? "ปลดล๊อคแนวรับ-ต้าน" : "ล๊อคแนวรับ-ต้าน"}
              >
                {isSRLocked ? '🔒' : '🔓'}
              </button>

            </div>


          </div>
        </div>






        {/* Desktop View: บรรทัดเดียว ไม่ต้องเลื่อน — กลุ่มที่มีตัวเลือกเยอะย่อเป็น dropdown/select ฟังก์ชันเดิม 100% */}
        <div className="hidden md:flex flex-nowrap items-center justify-center gap-1.5 text-xs">
          {/* Price Lines ถูกคอมเมนต์ออก เพราะ 'freestyle' เป็นโหมด default อยู่แล้ว (ไม่มีตัวเลือกอื่นให้สลับในตอนนี้) */}
          {/* <button
            className={`flex-shrink-0 px-2 py-1 rounded text-xs transition whitespace-nowrap ${priceLineType === 'freestyle' ? 'bg-yellow-300 text-gray-900 shadow' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'}`}
            onClick={() => setPriceLineType('freestyle')}
            title="Price Lines: Freestyle"
          >
            Freestyle
          </button> */}

          <div className="flex-shrink-0 flex items-center gap-1">
            <span className="text-gray-400 whitespace-nowrap">รูปแบบกราฟ:</span>
            <button className={`px-1.5 py-1 rounded text-xs transition whitespace-nowrap ${chartType === 'candlestick' ? 'bg-[var(--tab-selected)] text-gray-900 shadow' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'}`} onClick={() => setChartType('candlestick')} title="รูปแบบกราฟ: แท่งเทียน">แท่งเทียน</button>
            <button className={`px-1.5 py-1 rounded text-xs transition whitespace-nowrap ${chartType === 'heikin-ashi' ? 'bg-green-400 text-gray-900 shadow' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'}`} onClick={() => setChartType('heikin-ashi')} title="รูปแบบกราฟ: Heikin Ashi">Heikin Ashi</button>
          </div>

          {/* Overlays: ย่อเป็น dropdown checklist เดียว ประหยัดพื้นที่แต่กดใช้งานได้ครบทุกตัวเหมือนเดิม */}
          <div className="flex-shrink-0 relative pl-1.5 border-l border-gray-700" ref={overlaysMenuRef}>
            <button
              onClick={() => setShowOverlaysMenu(v => !v)}
              className={`px-2 py-1 rounded text-xs transition flex items-center gap-1 whitespace-nowrap ${showOverlaysMenu ? 'bg-gray-700 text-white' : 'bg-gray-800 text-gray-200 hover:bg-gray-700'}`}
            >
              Overlays
              {[showBollinger, showOverlays, showDashed, showSLine, showRLine, showTrendline].filter(Boolean).length > 0 && (
                <span className="bg-blue-500 text-white text-[10px] leading-none rounded-full px-1.5 py-0.5">
                  {[showBollinger, showOverlays, showDashed, showSLine, showRLine, showTrendline].filter(Boolean).length}
                </span>
              )}
              <span className="text-[9px]">▾</span>
            </button>
            {showOverlaysMenu && (
              <div className="absolute z-20 top-full mt-1 left-0 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-1.5 flex flex-col gap-0.5 min-w-[170px]">
                <label className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" className="cursor-pointer" checked={showBollinger} onChange={() => setShowBollinger(v => !v)} /> Bollinger Bands
                </label>
                <label className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" className="cursor-pointer" checked={showOverlays} onChange={() => setShowOverlays(v => !v)} /> ป้าย
                </label>
                <label className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" className="cursor-pointer" checked={showDashed} onChange={() => setShowDashed(v => !v)} /> เส้นประ
                </label>
                <label className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" className="cursor-pointer" checked={showSLine} onChange={() => setShowSLine(v => !v)} /> เส้นแนวรับ (S)
                </label>
                <label className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" className="cursor-pointer" checked={showRLine} onChange={() => setShowRLine(v => !v)} /> เส้นแนวต้าน (R)
                </label>
                <label className="flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-gray-700 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" className="cursor-pointer" checked={showTrendline} onChange={() => setShowTrendline(v => !v)} /> เส้นแนวโน้ม (Trend)
                </label>
              </div>
            )}
          </div>

          <div className="flex-shrink-0 flex items-center gap-1 pl-1.5 border-l border-gray-700">
            <span className="text-gray-400 whitespace-nowrap">ระยะ:</span>
            <select
              className="bg-gray-800 text-gray-200 text-xs px-1.5 py-1 rounded border border-gray-700 outline-none cursor-pointer"
              value={range}
              onChange={(e) => setRange(e.target.value as any)}
            >
              {/* {['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y'].map(r => (
                <option key={r} value={r}>{r}</option>
              ))} */}
                {['6M'].map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0 flex items-center gap-1 pl-1.5 border-l border-gray-700">
            <span className="text-gray-400 whitespace-nowrap">TF:</span>
            <select
              className="bg-gray-800 text-gray-200 text-xs px-1.5 py-1 rounded border border-gray-700 outline-none cursor-pointer"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as any)}
            >
              {['day', 'week'].map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0 flex items-center gap-1 pl-1.5 border-l border-gray-700">
            <span className="text-gray-400 whitespace-nowrap">lock:</span>
            <button
              className={`p-1.5 rounded-lg text-sm transition-all ${isSRLocked ? 'bg-yellow-500 text-black shadow-[0_0_10px_rgba(234,179,8,0.5)]' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
              onClick={() => setIsSRLocked(v => !v)}
              title={isSRLocked ? "ปลดล๊อคเส้นแนวรับ-ต้าน" : "ล๊อคเส้นแนวรับ-ต้าน"}
            >
              {isSRLocked ? '🔒' : '🔓'}
            </button>
          </div>
        </div>

        {/* Chart */}
        <div className="w-full flex justify-center border-t border-gray-700 pt-2">
          <div className="w-full h-full relative"
            style={{
              zIndex: 0, // ลด z-index หากจำเป็น
            }}
          >
            {ohlc.length > 0 ? (
              <>
                <StockChart
                  data={ohlc}
                  historicalData={historicalData}
                  chartType={chartType}
                  showBollinger={showBollinger}
                  priceLineType={priceLineType}
                  lowerIndicatorData={lowerIndicatorData}
                  zoomScope={range}
                  showOverlays={showOverlays}
                  onSupportResistanceUpdate={(data) => {
                    if (onSupportResistanceUpdate) onSupportResistanceUpdate(data);

                    if (canShowBtn) {
                      const lastExportedStr = localStorage.getItem(`last_exported_${symbol}`);
                      const currentDataArray = Array.isArray(data) ? data : [data];

                      // ✅ ฟังก์ชันช่วยเปิดปุ่มและเซฟสถานะลง Storage เฉพาะหุ้นตัวปัจจุบันเท่านั้น
                      const triggerDirty = () => {
                        // ถ้าไม่มีเส้นเลยและไม่เคยเซฟมาก่อน ไม่ต้องโชว์ปุ่ม
                        if (currentDataArray.length === 0 && !lastExportedStr) return;

                        setShowPublishBtn(true);
                        localStorage.setItem(`isDirty_${symbol}`, 'true');
                      };

                      if (!lastExportedStr) {
                        triggerDirty();
                        return;
                      }

                      try {
                        const lastExported = JSON.parse(lastExportedStr);

                        // 1. ดึงเฉพาะ "ราคา" ออกมา แล้วเรียงลำดับจากน้อยไปมาก (ตัดปัญหาเรื่อง Tag S/R สลับกัน)
                        const lastPrices = lastExported.map((item: any) => Number(item.price)).sort((a: any, b: any) => a - b);
                        const currentPrices = currentDataArray.map((item: any) => Number(item.price)).sort((a, b) => a - b);

                        // 2. ถ้าจำนวนเส้นไม่เท่ากัน = มีการลบหรือเพิ่มเส้นชัดเจน
                        if (lastPrices.length !== currentPrices.length) {
                          triggerDirty();
                          return;
                        }

                        // 3. เทียบราคาทีละคู่ในตำแหน่งเดียวกัน
                        let isActuallyChanged = false;
                        for (let i = 0; i < currentPrices.length; i++) {
                          // ถ้าราคาเส้นไหนขยับเกิน 0.01 ให้ถือว่ามีการเปลี่ยนแปลง
                          if (Math.abs(lastPrices[i] - currentPrices[i]) > 0.01) {
                            isActuallyChanged = true;
                            break;
                          }
                        }

                        if (isActuallyChanged) {
                          triggerDirty();
                        }
                      } catch (e) {
                        triggerDirty();
                      }
                    }
                  }}
                  onClearAllSR={onClearAllSR}
                  avgBuyPrice={avgBuyPrice}
                  showSLine={showSLine}
                  showRLine={showRLine}
                  showTrendline={showTrendline}
                  showDashed={showDashed}
                  frequency={frequency}
                  onIndicatorsReady={onIndicatorsReady} // <-- Add this line!
                  symbol={symbol}
                  setShowSLine={setShowSLine} // Pass setShowSLine
                  setShowRLine={setShowRLine} // Pass setShowRLine
                  setShowTrendline={setShowTrendline} // Pass setShowTrendline
                  mode={modeLoss} // ส่ง modeLoss ไปยัง StockChart
                  onPriceClick={onPriceClick} // Pass onPriceClick to StockChart
                  setModeLoss={setModeLoss} // Pass setModeLoss to StockChart
                  isSRLocked={isSRLocked}
                  // 🌟 2. ส่ง Props สำหรับรับ Chart กลับ และส่ง Event การเลื่อนกราฟ
                  setChartRef={(chart) => (chartsRef.current.stock = chart)}
                  onTimeRangeChange={(range) => handleTimeRangeChange('stock', range)}
                />
                {/* --- ปุ่มลูกศร เปิด-ปิด MACD/RSI (โชว์เฉพาะ Mobile) --- */}
                <div className="md:hidden mt-4 border-t border-gray-800 pt-2">
                  <button
                    onClick={() => setShowIndicatorsMobile(!showIndicatorsMobile)}
                    className="w-full flex justify-between items-center p-3 bg-gray-800/50 rounded-lg text-[var(--text-color)] font-bold text-sm transition-all active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      📊 {showIndicatorsMobile ? 'ซ่อน' : 'แสดง'} ตัวชี้วัดเพิ่มเติม (MACD, RSI)
                    </span>
                    <span className={`text-xl transition-transform duration-300 ${showIndicatorsMobile ? 'rotate-180' : 'rotate-0'}`}>
                      ▼
                    </span>
                  </button>

                </div>

                {/* --- ส่วนแสดง MACD และ RSI --- */}
                {/* บน Desktop (md:) จะโชว์เสมอ (block) | บน Mobile จะขึ้นอยู่กับ State (hidden/block) */}
                <div className={`${showIndicatorsMobile ? 'block' : 'hidden'} md:block mt-2 space-y-2`}>
                  <div className="rounded-xl bg-black/10 p-1">
                    <RSIChart
                      data={ohlc}
                      showOverlays={showOverlays}
                      showDashed={showDashed}
                      setChartRef={(chart) => (chartsRef.current.rsi = chart)}
                      onTimeRangeChange={(range) => handleTimeRangeChange('rsi', range)} />
                  </div>
                  <div className="rounded-xl bg-black/10 p-1">
                    <MACDChart 
                    data={ohlc} 
                    showOverlays={showOverlays} 
                    showDashed={showDashed} 
                    setChartRef={(chart) => (chartsRef.current.macd = chart)}
                    onTimeRangeChange={(range) => handleTimeRangeChange('macd', range)}
                    />

                  </div>
                </div>
              </>
            ) : (
              <p className="text-gray-400">Loading chart...</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingView;